import { ref, onMounted, nextTick, watch } from 'vue'
import { useSettingIntegrationsStore } from '@/store/modules/integrations'
import { getStorefrontAuth } from '@/api/storefront-auth'
import { validatePixel, type PixelValidateResult } from '@/api/pixel'
import { debounce } from 'lodash-es'
import { useShopStore } from '@/store/modules/payment'

export interface ApplovinInputItem {
  productVendor: string
  productCategory: string
}

const normalizePairedStringArray = (
  tokens: string[] | string | undefined,
  pairCount: number
): string[] => {
  const count = Math.max(pairCount, 1)
  let normalized: string[] = ['']

  if (Array.isArray(tokens) && tokens.length > 0) {
    normalized = [...tokens]
  } else if (typeof tokens === 'string' && tokens.trim()) {
    normalized = [tokens.trim()]
  }

  while (normalized.length < count) {
    normalized.push('')
  }
  if (normalized.length > count) {
    normalized = normalized.slice(0, count)
  }
  return normalized
}

const normalizeGoogleAdsGIds = (
  gIds: string[] | string | undefined
): string[] => {
  if (
    Array.isArray(gIds) &&
    gIds.length > 0 &&
    gIds.some((id) => id && id.trim() !== '')
  ) {
    return gIds
  }
  if (typeof gIds === 'string' && gIds.trim()) {
    return [gIds.trim()]
  }
  return ['']
}

const normalizeGoogleAdsConversionSendTos = (
  sendTos: string[] | string | undefined,
  gIdCount: number
): string[] => normalizePairedStringArray(sendTos, gIdCount)

const resolveGoogleAdsFields = (source?: {
  googleAdsGIds?: string[] | string
  googleAdsGId?: string
  googleAdsConversionSendTos?: string[] | string
  googleAdsConversionSendTo?: string
}) => {
  const googleAdsGIds = normalizeGoogleAdsGIds(source?.googleAdsGIds ?? source?.googleAdsGId)
  const googleAdsConversionSendTos = normalizeGoogleAdsConversionSendTos(
    source?.googleAdsConversionSendTos ?? source?.googleAdsConversionSendTo,
    googleAdsGIds.length
  )
  return { googleAdsGIds, googleAdsConversionSendTos }
}

const normalizeMediaGoAdIds = (
  adIds: string[] | string | undefined
): string[] => {
  if (
    Array.isArray(adIds) &&
    adIds.length > 0 &&
    adIds.some((id) => id && id.trim() !== '')
  ) {
    return adIds
  }
  if (typeof adIds === 'string' && adIds.trim()) {
    return [adIds.trim()]
  }
  return ['']
}

const normalizeMediaGoAccountNames = (
  accountNames: string[] | string | undefined,
  adIdCount: number
): string[] => normalizePairedStringArray(accountNames, adIdCount)

const resolveMediaGoFields = (source?: {
  mediaGoAdIds?: string[] | string
  mediaGoAdId?: string
  mediaGoAccountNames?: string[] | string
  mediaGoAccountName?: string
}) => {
  const mediaGoAdIds = normalizeMediaGoAdIds(source?.mediaGoAdIds ?? source?.mediaGoAdId)
  const mediaGoAccountNames = normalizeMediaGoAccountNames(
    source?.mediaGoAccountNames ?? source?.mediaGoAccountName,
    mediaGoAdIds.length
  )
  return { mediaGoAdIds, mediaGoAccountNames }
}

const normalizeTikTokConversionAccessTokens = (
  tokens: string[] | string | undefined,
  pixelCount: number
): string[] => {
  const count = Math.max(pixelCount, 1)
  let normalized: string[] = ['']

  if (Array.isArray(tokens) && tokens.length > 0) {
    normalized = [...tokens]
  } else if (typeof tokens === 'string' && tokens.trim()) {
    normalized = [tokens.trim()]
  }

  while (normalized.length < count) {
    normalized.push('')
  }
  if (normalized.length > count) {
    normalized = normalized.slice(0, count)
  }
  return normalized
}

const normalizePinterestAccountIds = (
  accountIds: string[] | string | undefined
): string[] => {
  if (
    Array.isArray(accountIds) &&
    accountIds.length > 0 &&
    accountIds.some((id) => id && id.trim() !== '')
  ) {
    return accountIds
  }
  if (typeof accountIds === 'string' && accountIds.trim()) {
    return [accountIds.trim()]
  }
  return ['']
}

const normalizePinterestConversionAccessTokens = (
  tokens: string[] | string | undefined,
  accountCount: number
): string[] => {
  const count = Math.max(accountCount, 1)
  let normalized: string[] = ['']

  if (Array.isArray(tokens) && tokens.length > 0) {
    normalized = [...tokens]
  } else if (typeof tokens === 'string' && tokens.trim()) {
    normalized = [tokens.trim()]
  }

  while (normalized.length < count) {
    normalized.push('')
  }
  if (normalized.length > count) {
    normalized = normalized.slice(0, count)
  }
  return normalized
}

const normalizeSnapchatPixelIds = (
  pixelIds: string[] | string | undefined,
  snapchatPixels?: Array<{ pixel_id?: string; pixelId?: string; token?: string }>
): string[] => {
  if (
    Array.isArray(pixelIds) &&
    pixelIds.length > 0 &&
    pixelIds.some((id) => id && id.trim() !== '')
  ) {
    return pixelIds
  }
  if (typeof pixelIds === 'string' && pixelIds.trim()) {
    return [pixelIds.trim()]
  }
  if (Array.isArray(snapchatPixels) && snapchatPixels.length > 0) {
    const ids = snapchatPixels
      .map((item) => (item?.pixel_id || item?.pixelId || '').trim())
      .filter(Boolean)
    if (ids.length > 0) {
      return ids
    }
  }
  return ['']
}

const normalizeSnapchatConversionApiTokens = (
  tokens: string[] | string | undefined,
  pixelCount: number,
  snapchatPixels?: Array<{ token?: string }>
): string[] => {
  const count = Math.max(pixelCount, 1)
  let normalized: string[] = ['']

  if (Array.isArray(tokens) && tokens.length > 0) {
    normalized = [...tokens]
  } else if (typeof tokens === 'string' && tokens.trim()) {
    normalized = [tokens.trim()]
  } else if (Array.isArray(snapchatPixels) && snapchatPixels.length > 0) {
    normalized = snapchatPixels.map((item) => (item?.token || '').trim())
  }

  while (normalized.length < count) {
    normalized.push('')
  }
  if (normalized.length > count) {
    normalized = normalized.slice(0, count)
  }
  return normalized
}

const resolveSnapchatFields = (source?: {
  snapchatPixelIds?: string[] | string
  snapchatPixelId?: string
  snapchatConversionApiTokens?: string[] | string
  snapchatConversionApiToken?: string
  snapchatPixels?: Array<{ pixel_id?: string; pixelId?: string; token?: string }>
}) => {
  const snapchatPixelIds = normalizeSnapchatPixelIds(
    source?.snapchatPixelIds ?? source?.snapchatPixelId,
    source?.snapchatPixels
  )
  const snapchatConversionApiTokens = normalizeSnapchatConversionApiTokens(
    source?.snapchatConversionApiTokens ?? source?.snapchatConversionApiToken,
    snapchatPixelIds.length,
    source?.snapchatPixels
  )
  return { snapchatPixelIds, snapchatConversionApiTokens }
}

const normalizeSmartNewsPartnerNames = (
  partnerNames: string[] | string | undefined
): string[] => {
  if (
    Array.isArray(partnerNames) &&
    partnerNames.length > 0 &&
    partnerNames.some((name) => name && name.trim() !== '')
  ) {
    return partnerNames
  }
  if (typeof partnerNames === 'string' && partnerNames.trim()) {
    return [partnerNames.trim()]
  }
  return ['']
}

const normalizeSmartNewsPixelTagIds = (
  pixelTagIds: string[] | string | undefined,
  partnerCount: number
): string[] => {
  const count = Math.max(partnerCount, 1)
  let normalized: string[] = ['']

  if (Array.isArray(pixelTagIds) && pixelTagIds.length > 0) {
    normalized = [...pixelTagIds]
  } else if (typeof pixelTagIds === 'string' && pixelTagIds.trim()) {
    normalized = [pixelTagIds.trim()]
  }

  while (normalized.length < count) {
    normalized.push('')
  }
  if (normalized.length > count) {
    normalized = normalized.slice(0, count)
  }
  return normalized
}

const resolveApplovinFields = (source?: {
  applovinPixelId?: string[] | string
  applovinPixelEventKey?: string[] | string
}) => {
  // AppLovin 不支持多组：UI 仍用单元素数组展示，读取时只取第一项
  const toFirst = (values: string[] | string | undefined): string => {
    if (Array.isArray(values)) {
      const firstNonEmpty = values.find((value) => value && value.trim() !== '')
      return (firstNonEmpty ?? values[0] ?? '').trim()
    }
    if (typeof values === 'string') {
      return values.trim()
    }
    return ''
  }

  return {
    applovinPixelIds: [toFirst(source?.applovinPixelId)],
    applovinPixelEventKeys: [toFirst(source?.applovinPixelEventKey)],
  }
}

// 全局状态，确保所有组件实例共享同一个状态
export const globalHasUnsavedChanges = ref(false)
// 跨实例的全局校验去重与节流
const globalValidationInFlight = ref(false)
const globalLastValidatedToken = ref('')
const globalPrivateValidationInFlight = ref(false)
const globalLastValidatedPrivateToken = ref('')

export const useIntegrations = () => {
  // Initialize state with default values
  const state = ref({
    newsBreakPixelInputType: 'pixel',
    showFacebookPixelAdvancedSettings: false,
    showTikTokPixelAdvancedSettings: false,
    showApplovinPixeAdvancedSettings: false,
    showNewsBreakTestPixel: false,
    facebookPixelInputDisabled: true,
    tikTokPixelInputDisabled: true,
    newsBreakPixelInputDisabled: true,
    bigoPixelInputDisabled: true,
    roaiPixelInputDisabled: true,
    pinterestInputDisabled: true,
    snapchatInputDisabled: true,
    smartNewsInputDisabled: true,
    mediaGoInputDisabled: true,
    googleAdsInputDisabled: true,
    applovinPixelInputDisabled: true,
    extensionCodeInputDisabled: true,
    isEnableConversionAPI: false,
    isEnablePostPurchaseEvent: false,
    isEnableGoogleAdsPostPurchaseEvent: false,
    isEnableExtensionCode: false,
    isEnableShippingCostInPurchaseEvents: false,
    isEnableEventPixelDetails: false,
    facebookADM: 'Disabled',
    tikTokADM: 'Default Level',
    applovinADM: 'Disabled',
    facebookPixelInputs: [''] as string[],
    tikTokPixelInputs: [''] as string[],
    tikTokConversionAccessTokens: [''] as string[],
    newsBreakPixelInputs: [''] as string[],
    bigoPixelInputs: [''] as string[],
    roaiPixelInputs: [''] as string[],
    pinterestAccountIds: [''] as string[],
    pinterestConversionAccessTokens: [''] as string[],
    snapchatPixelIds: [''] as string[],
    snapchatConversionApiTokens: [''] as string[],
    smartNewsPartnerNames: [''] as string[],
    smartNewsPixelTagIds: [''] as string[],
    smartNewsAuthorization: '',
    mediaGoAdIds: [''] as string[],
    mediaGoAccountNames: [''] as string[],
    mediaGoAdId: '',
    mediaGoAccountName: '',
    googleAdsGIds: [''] as string[],
    googleAdsConversionSendTos: [''] as string[],
    googleAdsGId: '',
    googleAdsConversionSendTo: '',
    applovinPixelInputs: [
      { productVendor: '', productCategory: '' },
    ] as ApplovinInputItem[],
    applovinPixelIds: [''] as string[],
    applovinPixelEventKeys: [''] as string[],
    isEnableApplovinBackendReport: false,
    facebookItems: [
      { label: 'Disabled', value: 'Disabled' },
      { label: 'Default Level', value: 'Default Level' },
      { label: 'Maximum Level', value: 'Maximum Level' },
    ],
    tikTokItems: [
      { label: 'Disabled', value: 'Disabled' },
      { label: 'Default Level', value: 'Default Level' },
    ],
    applovinItems: [
      { label: 'Disabled', value: 'Disabled' },
      { label: 'Default Level', value: 'Default Level' },
    ],
    postbackValuePercentage: 100,
    extensionCode: '',
    googlePlacesApiKey: '',
    facebookConversionApiTokens: [''] as string[],
    isEnableNBConversionAPI: false,
    isEnableBigoBackendReport: false,
    isEnablePinterestBackendConversion: false,
    isEnableSnapchatBackendReport: false,
    isEnableSmartNewsBackendConversion: false,
    isEnableMediaGoBackendConversion: false,
    isEnableTikTokBackendReport: false,
  })

  const token = ref('')
  const privateToken = ref('')
  const isChecking = ref(false)
  const isCheckingPrivate = ref(false)
  const isValidToken = ref(false)
  const isValidPrivateToken = ref(false)
  const validationMessage = ref('')
  const privateValidationMessage = ref('')
  const lastValidatedToken = ref('')
  const lastValidatedPrivateToken = ref('')
  const isInitialValidating = ref(false)
  const isMounted = ref(false)
  const isInitializing = ref(true)
  const settingStore = ref<ReturnType<typeof useSettingIntegrationsStore> | null>(null)
  const hasUnsavedChanges = globalHasUnsavedChanges
  const isDataLoading = ref(false) // 新增：标记是否正在加载数据
  const initialToken = ref('') // 记录初始token值，用于判断是否真正改变
  const initialPrivateToken = ref('')
  const hasUserModifiedToken = ref(false) // 标记用户是否主动修改过token
  const hasUserModifiedPrivateToken = ref(false)
  const newsBreakTestAdId = ref('')
  const isCheckingNewsBreakPixel = ref(false)
  const isValidNewsBreakPixel = ref(false)
  const newsBreakPixelValidationMessage = ref('')

  // Initialize store values
  const integrationsState = ref({
    isEnableConversionAPI: false,
    isEnableEventPixelDetails: false,
    isEnableShippingCostInPurchaseEvents: false,
    isEnablePostPurchaseEvent: false,
    isEnableGoogleAdsPostPurchaseEvent: false,
    facebookADM: 'Disabled',
    tikTokADM: 'Default Level',
    facebookPixels: [''] as string[],
    tikTokPixels: [''] as string[],
    tikTokConversionAccessTokens: [''] as string[],
    newsBreakPixels: [''] as string[],
    newsBreakPixelInputType: 'pixel' as string,
    bigoPixels: [''] as string[],
    roaiPixels: [''] as string[],
    pinterestAccountIds: [''] as string[],
    pinterestConversionAccessTokens: [''] as string[],
    snapchatPixelIds: [''] as string[],
    snapchatConversionApiTokens: [''] as string[],
    smartNewsPartnerNames: [''] as string[],
    smartNewsPixelTagIds: [''] as string[],
    smartNewsAuthorization: '',
    mediaGoAdIds: [''] as string[],
    mediaGoAccountNames: [''] as string[],
    mediaGoAdId: '',
    mediaGoAccountName: '',
    googleAdsGIds: [''] as string[],
    googleAdsConversionSendTos: [''] as string[],
    googleAdsGId: '',
    googleAdsConversionSendTo: '',
    applovinPixelInputs: [
      { productVendor: '', productCategory: '' },
    ] as ApplovinInputItem[],
    applovinPixelId: '',
    applovinPixelEventKey: '',
    isEnableApplovinBackendReport: false,
    postbackValuePercentage: 100,
    extensionCode: '',
    extensionCodeInputDisabled: true,
    isEnableExtensionCode: false,
    storefrontToken: '',
    storefrontApiPrivateToken: '',
    isValidToken: false,
    isValidPrivateToken: false,
    googlePlacesApiKey: '',
    facebookConversionApiTokens: [''] as string[],
    isEnableNBConversionAPI: false,
    isEnableBigoBackendReport: false,
    isEnablePinterestBackendConversion: false,
    isEnableSnapchatBackendReport: false,
    isEnableSmartNewsBackendConversion: false,
    isEnableMediaGoBackendConversion: false,
    isEnableTikTokBackendReport: false,
  })

  // Set mounted state and initialize store
  onMounted(async () => {
    const shopStore = useShopStore()
    // 仅当还没有店铺详情时才拉取，避免重复请求
    if (!shopStore.shopDetail || !(shopStore.shopDetail as any).id) {
      await shopStore.fetchShopDetail()
    }
    isMounted.value = true

    nextTick(() => {
      try {
        // 在开始加载数据之前就设置标志位，确保 watcher 不会触发
        isDataLoading.value = true
        isInitializing.value = true
        isInitializingData.value = true
        settingStore.value = useSettingIntegrationsStore()
        if (settingStore.value) {
          // 从shopApi中获取 storefront tokens
          let storefrontToken = ''
          let storefrontApiPrivateToken = ''
          if (shopStore.shopDetail?.shopApi) {
            try {
              const shopApi = JSON.parse(shopStore.shopDetail.shopApi)
              storefrontToken = shopApi.storefrontApiAccessToken || ''
              storefrontApiPrivateToken = shopApi.storefrontApiPrivateToken || ''
            } catch (error) {
              console.error('Error parsing shopApi:', error)
            }
          }

          // 优先使用后台返回的数据初始化表单
          if (shopStore.shopDetail?.intergrations) {
            const intergrations = JSON.parse(shopStore.shopDetail?.intergrations || '{}')
            const tikTokPixelInputs =
              intergrations?.tikTokPixels &&
              intergrations.tikTokPixels.length > 0 &&
              intergrations.tikTokPixels.some((pixel: string) => pixel && pixel.trim() !== '')
                ? intergrations.tikTokPixels
                : ['']
            const tikTokConversionAccessTokens = normalizeTikTokConversionAccessTokens(
              intergrations?.tikTokConversionAccessTokens ??
                intergrations?.tikTokConversionAccessToken,
              tikTokPixelInputs.length
            )

            const pinterestAccountIds = normalizePinterestAccountIds(
              intergrations?.pinterestAccountIds ?? intergrations?.pinterestAccountId
            )
            const pinterestConversionAccessTokens = normalizePinterestConversionAccessTokens(
              intergrations?.pinterestConversionAccessTokens ??
                intergrations?.pinterestConversionAccessToken,
              pinterestAccountIds.length
            )
            const { snapchatPixelIds, snapchatConversionApiTokens } = resolveSnapchatFields(intergrations)
            const smartNewsPartnerNames = normalizeSmartNewsPartnerNames(
              intergrations?.smartNewsPartnerNames ?? intergrations?.smartNewsPartnerName
            )
            const smartNewsPixelTagIds = normalizeSmartNewsPixelTagIds(
              intergrations?.smartNewsPixelTagIds ?? intergrations?.smartNewsPixelTagId,
              smartNewsPartnerNames.length
            )
            const { mediaGoAdIds, mediaGoAccountNames } = resolveMediaGoFields(intergrations)
            const { googleAdsGIds, googleAdsConversionSendTos } = resolveGoogleAdsFields(intergrations)
            const { applovinPixelIds, applovinPixelEventKeys } = resolveApplovinFields(intergrations)

            // 更新 state
            state.value = {
              ...state.value,
              facebookPixelInputs: intergrations?.facebookPixels && intergrations.facebookPixels.length > 0 && intergrations.facebookPixels.some((pixel: string) => pixel && pixel.trim() !== '') ? intergrations.facebookPixels : [''],
              facebookPixelInputDisabled: true, // 确保保持禁用状态
              tikTokPixelInputs,
              tikTokPixelInputDisabled: true, // 确保保持禁用状态
              tikTokConversionAccessTokens,
              newsBreakPixelInputs: intergrations?.newsBreakPixels && intergrations.newsBreakPixels.length > 0 && intergrations.newsBreakPixels.some((pixel: string) => pixel && pixel.trim() !== '') ? intergrations.newsBreakPixels : [''],
              newsBreakPixelInputType: intergrations?.newsBreakPixelInputType || 'pixel',
              newsBreakPixelInputDisabled: true, // 确保保持禁用状态
              bigoPixelInputs: intergrations?.bigoPixels && intergrations.bigoPixels.length > 0 && intergrations.bigoPixels.some((pixel: string) => pixel && pixel.trim() !== '') ? intergrations.bigoPixels : [''],
              bigoPixelInputDisabled: true, // 确保保持禁用状态
              roaiPixelInputs: intergrations?.roaiPixels && intergrations.roaiPixels.length > 0 && intergrations.roaiPixels.some((pixel: string) => pixel && pixel.trim() !== '') ? intergrations.roaiPixels : [''],
              roaiPixelInputDisabled: true, // 确保保持禁用状态
              pinterestInputDisabled: true,
              pinterestAccountIds,
              pinterestConversionAccessTokens,
              snapchatInputDisabled: true,
              snapchatPixelIds,
              snapchatConversionApiTokens,
              smartNewsInputDisabled: true,
              smartNewsPartnerNames,
              smartNewsPixelTagIds,
              smartNewsAuthorization: intergrations?.smartNewsAuthorization || '',
              mediaGoInputDisabled: true,
              mediaGoAdIds,
              mediaGoAccountNames,
              googleAdsInputDisabled: true,
              googleAdsGIds,
              googleAdsConversionSendTos,
              isEnableGoogleAdsPostPurchaseEvent:
                intergrations?.isEnableGoogleAdsPostPurchaseEvent || false,
              applovinPixelInputs: intergrations?.applovinPixelInputs?.length ? intergrations.applovinPixelInputs : [{ productVendor: '', productCategory: '' }],
              applovinPixelIds,
              applovinPixelEventKeys,
              isEnableApplovinBackendReport: intergrations?.isEnableApplovinBackendReport === true,
              postbackValuePercentage: intergrations?.postbackValuePercentage ?? 100,
              extensionCode: intergrations?.extensionCode || '',
              // 始终默认禁用，用户需主动点击 Change 才能编辑
              extensionCodeInputDisabled: true,
              isEnableExtensionCode: intergrations?.isEnableExtensionCode || false,
              googlePlacesApiKey: intergrations?.googlePlacesApiKey || '',
              isEnableConversionAPI: intergrations?.isEnableConversionAPI || false,
              isEnableEventPixelDetails: intergrations?.isEnableEventPixelDetails || false,
              isEnablePostPurchaseEvent: intergrations?.isEnablePostPurchaseEvent || false,
              isEnableShippingCostInPurchaseEvents:
                intergrations?.isEnableShippingCostInPurchaseEvents || false,
              facebookADM: intergrations?.facebookADM || 'Disabled',
              tikTokADM: intergrations?.tikTokADM || 'Disabled',
              facebookConversionApiTokens: intergrations?.facebookConversionApiTokens || [''],
              isEnableNBConversionAPI: intergrations?.isEnableNBConversionAPI || false,
              isEnableBigoBackendReport: intergrations?.isEnableBigoBackendReport === true,
              isEnablePinterestBackendConversion:
                intergrations?.isEnablePinterestBackendConversion === true,
              isEnableSnapchatBackendReport:
                intergrations?.isEnableSnapchatBackendReport === true ||
                intergrations?.isEnableSnapchatConversionAPI === true,
              isEnableSmartNewsBackendConversion:
                intergrations?.isEnableSmartNewsBackendConversion === true,
              isEnableMediaGoBackendConversion:
                intergrations?.isEnableMediaGoBackendConversion === true,
              isEnableTikTokBackendReport: intergrations?.isEnableTikTokBackendReport === true,
            }

            // 更新 integrationsState - 使用从shopApi获取的storefrontToken
            integrationsState.value = {
              ...integrationsState.value,
              storefrontToken: storefrontToken || intergrations?.storefrontToken || '',
              storefrontApiPrivateToken:
                storefrontApiPrivateToken || intergrations?.storefrontApiPrivateToken || '',
              facebookPixels: intergrations?.facebookPixels || [''],
              tikTokPixels: tikTokPixelInputs,
              tikTokConversionAccessTokens,
              newsBreakPixels: intergrations?.newsBreakPixels || [''],
              newsBreakPixelInputType: intergrations?.newsBreakPixelInputType || 'pixel',
              bigoPixels: intergrations?.bigoPixels || [''],
              roaiPixels: intergrations?.roaiPixels || [''],
              pinterestAccountIds,
              pinterestConversionAccessTokens,
              snapchatPixelIds,
              snapchatConversionApiTokens,
              smartNewsPartnerNames,
              smartNewsPixelTagIds,
              smartNewsAuthorization: intergrations?.smartNewsAuthorization || '',
              mediaGoAdIds,
              mediaGoAccountNames,
              googleAdsGIds,
              googleAdsConversionSendTos,
              isEnableGoogleAdsPostPurchaseEvent:
                intergrations?.isEnableGoogleAdsPostPurchaseEvent || false,
              applovinPixelInputs: intergrations?.applovinPixelInputs || [
                { productVendor: '', productCategory: '' },
              ],
              applovinPixelId: applovinPixelIds[0] || '',
              applovinPixelEventKey: applovinPixelEventKeys[0] || '',
              isEnableApplovinBackendReport: intergrations?.isEnableApplovinBackendReport === true,
              postbackValuePercentage: intergrations?.postbackValuePercentage || 100,
              extensionCode: intergrations?.extensionCode || '',
              // 保持与 state 一致，进入页面时统一默认禁用
              extensionCodeInputDisabled: true,
              isEnableExtensionCode: intergrations?.isEnableExtensionCode || false,
              isEnableConversionAPI: intergrations?.isEnableConversionAPI || false,
              isEnableEventPixelDetails: intergrations?.isEnableEventPixelDetails || false,
              isEnablePostPurchaseEvent: intergrations?.isEnablePostPurchaseEvent || false,
              isEnableShippingCostInPurchaseEvents:
                intergrations?.isEnableShippingCostInPurchaseEvents || false,
              facebookADM: intergrations?.facebookADM || 'Disabled',
              tikTokADM: intergrations?.tikTokADM || 'Disabled',
              isValidToken: intergrations?.isValidToken || false,
              isValidPrivateToken: intergrations?.isValidPrivateToken || false,
              googlePlacesApiKey: intergrations?.googlePlacesApiKey || '',
              facebookConversionApiTokens: intergrations?.facebookConversionApiTokens || [''],
              isEnableNBConversionAPI: intergrations?.isEnableNBConversionAPI || false,
              isEnableBigoBackendReport: intergrations?.isEnableBigoBackendReport === true,
              isEnablePinterestBackendConversion:
                intergrations?.isEnablePinterestBackendConversion === true,
              isEnableSnapchatBackendReport:
                intergrations?.isEnableSnapchatBackendReport === true ||
                intergrations?.isEnableSnapchatConversionAPI === true,
              isEnableSmartNewsBackendConversion:
                intergrations?.isEnableSmartNewsBackendConversion === true,
              isEnableMediaGoBackendConversion:
                intergrations?.isEnableMediaGoBackendConversion === true,
              isEnableTikTokBackendReport: intergrations?.isEnableTikTokBackendReport === true,
            }

            // 更新 token 和 isValidToken - 使用从shopApi获取的storefrontToken
            token.value = storefrontToken || intergrations?.storefrontToken || ''
            privateToken.value =
              storefrontApiPrivateToken || intergrations?.storefrontApiPrivateToken || ''
            isValidToken.value = intergrations?.isValidToken || false
            isValidPrivateToken.value = intergrations?.isValidPrivateToken || false

            // 更新 store - 使用从shopApi获取的storefrontToken
            settingStore.value.setIntegrationsForm({
              storefrontToken: storefrontToken || intergrations?.storefrontToken || '',
              storefrontApiPrivateToken:
                storefrontApiPrivateToken || intergrations?.storefrontApiPrivateToken || '',
              facebookPixels: intergrations?.facebookPixels || [],
              tikTokPixels: tikTokPixelInputs,
              tikTokConversionAccessTokens,
              newsBreakPixels: intergrations?.newsBreakPixels || [],
              newsBreakPixelInputType: intergrations?.newsBreakPixelInputType || 'pixel',
              bigoPixels: intergrations?.bigoPixels || [],
              roaiPixels: intergrations?.roaiPixels || [],
              pinterestAccountIds,
              pinterestConversionAccessTokens,
              snapchatPixelIds,
              snapchatConversionApiTokens,
              smartNewsPartnerNames,
              smartNewsPixelTagIds,
              smartNewsAuthorization: intergrations?.smartNewsAuthorization || '',
              mediaGoAdIds,
              mediaGoAccountNames,
              mediaGoAdId: mediaGoAdIds[0] || '',
              mediaGoAccountName: mediaGoAccountNames[0] || '',
              googleAdsGIds,
              googleAdsConversionSendTos,
              googleAdsGId: googleAdsGIds[0] || '',
              googleAdsConversionSendTo: googleAdsConversionSendTos[0] || '',
              isEnableGoogleAdsPostPurchaseEvent:
                intergrations?.isEnableGoogleAdsPostPurchaseEvent || false,
              applovinPixelInputs: intergrations?.applovinPixelInputs?.length ? intergrations.applovinPixelInputs : [{ productVendor: '', productCategory: '' }],
              applovinPixelId: applovinPixelIds[0] || '',
              applovinPixelEventKey: applovinPixelEventKeys[0] || '',
              isEnableApplovinBackendReport: intergrations?.isEnableApplovinBackendReport === true,
              postbackValuePercentage: intergrations?.postbackValuePercentage ?? 100,
              extensionCode: intergrations?.extensionCode || '',
              isEnableExtensionCode: intergrations?.isEnableExtensionCode || false,
              googlePlacesApiKey: intergrations?.googlePlacesApiKey || '',
              isEnableConversionAPI: intergrations?.isEnableConversionAPI || false,
              isEnableEventPixelDetails: intergrations?.isEnableEventPixelDetails || false,
              isEnablePostPurchaseEvent: intergrations?.isEnablePostPurchaseEvent || false,
              isEnableShippingCostInPurchaseEvents:
                intergrations?.isEnableShippingCostInPurchaseEvents || false,
              facebookADM: intergrations?.facebookADM || 'Disabled',
              tikTokADM: intergrations?.tikTokADM || 'Disabled',
              facebookConversionApiTokens: intergrations?.facebookConversionApiTokens || [''],
              isValidToken: intergrations.isValidToken || false,
              isValidPrivateToken: intergrations?.isValidPrivateToken || false,
              isEnableNBConversionAPI: intergrations?.isEnableNBConversionAPI || false,
              isEnableBigoBackendReport: intergrations?.isEnableBigoBackendReport === true,
              isEnablePinterestBackendConversion:
                intergrations?.isEnablePinterestBackendConversion === true,
              isEnableSnapchatBackendReport:
                intergrations?.isEnableSnapchatBackendReport === true ||
                intergrations?.isEnableSnapchatConversionAPI === true,
              isEnableSmartNewsBackendConversion:
                intergrations?.isEnableSmartNewsBackendConversion === true,
              isEnableMediaGoBackendConversion:
                intergrations?.isEnableMediaGoBackendConversion === true,
              isEnableTikTokBackendReport: intergrations?.isEnableTikTokBackendReport === true,
            })
          } else {
            // 如果没有后台数据，则使用 store 中的数据
            const tikTokPixelInputs =
              settingStore.value.integrationsForm?.tikTokPixels &&
              settingStore.value.integrationsForm?.tikTokPixels.length > 0 &&
              settingStore.value.integrationsForm?.tikTokPixels.some(
                (pixel: string) => pixel && pixel.trim() !== ''
              )
                ? settingStore.value.integrationsForm.tikTokPixels
                : ['']
            const tikTokConversionAccessTokens = normalizeTikTokConversionAccessTokens(
              settingStore.value.integrationsForm?.tikTokConversionAccessTokens ??
                (settingStore.value.integrationsForm as { tikTokConversionAccessToken?: string })
                  ?.tikTokConversionAccessToken,
              tikTokPixelInputs.length
            )
            const pinterestAccountIds = normalizePinterestAccountIds(
              settingStore.value.integrationsForm?.pinterestAccountIds ??
                (settingStore.value.integrationsForm as { pinterestAccountId?: string })
                  ?.pinterestAccountId
            )
            const pinterestConversionAccessTokens = normalizePinterestConversionAccessTokens(
              settingStore.value.integrationsForm?.pinterestConversionAccessTokens ??
                (settingStore.value.integrationsForm as { pinterestConversionAccessToken?: string })
                  ?.pinterestConversionAccessToken,
              pinterestAccountIds.length
            )
            const { snapchatPixelIds, snapchatConversionApiTokens } = resolveSnapchatFields(
              settingStore.value.integrationsForm
            )
            const smartNewsPartnerNames = normalizeSmartNewsPartnerNames(
              settingStore.value.integrationsForm?.smartNewsPartnerNames ??
                (settingStore.value.integrationsForm as { smartNewsPartnerName?: string })
                  ?.smartNewsPartnerName
            )
            const smartNewsPixelTagIds = normalizeSmartNewsPixelTagIds(
              settingStore.value.integrationsForm?.smartNewsPixelTagIds ??
                (settingStore.value.integrationsForm as { smartNewsPixelTagId?: string })
                  ?.smartNewsPixelTagId,
              smartNewsPartnerNames.length
            )
            const { mediaGoAdIds, mediaGoAccountNames } = resolveMediaGoFields(
              settingStore.value.integrationsForm
            )
            const { googleAdsGIds, googleAdsConversionSendTos } = resolveGoogleAdsFields(
              settingStore.value.integrationsForm
            )
            const { applovinPixelIds, applovinPixelEventKeys } = resolveApplovinFields(
              settingStore.value.integrationsForm
            )
            state.value = {
              ...state.value,
              isEnableConversionAPI: settingStore.value.integrationsForm.isEnableConversionAPI,
              isEnableNBConversionAPI: settingStore.value.integrationsForm.isEnableNBConversionAPI,
              isEnableBigoBackendReport:
                settingStore.value.integrationsForm.isEnableBigoBackendReport === true,
              isEnablePinterestBackendConversion:
                settingStore.value.integrationsForm.isEnablePinterestBackendConversion === true,
              isEnableSnapchatBackendReport:
                settingStore.value.integrationsForm.isEnableSnapchatBackendReport === true,
              isEnableSmartNewsBackendConversion:
                settingStore.value.integrationsForm.isEnableSmartNewsBackendConversion === true,
              isEnableMediaGoBackendConversion:
                settingStore.value.integrationsForm.isEnableMediaGoBackendConversion === true,
              isEnableTikTokBackendReport:
                settingStore.value.integrationsForm.isEnableTikTokBackendReport === true,
              isEnableEventPixelDetails:
                settingStore.value.integrationsForm.isEnableEventPixelDetails,
              isEnableShippingCostInPurchaseEvents:
                settingStore.value.integrationsForm?.isEnableShippingCostInPurchaseEvents,
              facebookADM: settingStore.value.integrationsForm?.facebookADM,
              tikTokADM: settingStore.value.integrationsForm?.tikTokADM,
              facebookPixelInputs: settingStore.value.integrationsForm?.facebookPixels && settingStore.value.integrationsForm?.facebookPixels.length > 0 && settingStore.value.integrationsForm?.facebookPixels.some((pixel: string) => pixel && pixel.trim() !== '') ? settingStore.value.integrationsForm?.facebookPixels : [''],
              facebookPixelInputDisabled: true, // 确保保持禁用状态
              tikTokPixelInputs,
              tikTokPixelInputDisabled: true, // 确保保持禁用状态
              tikTokConversionAccessTokens,
              newsBreakPixelInputs: settingStore.value.integrationsForm?.newsBreakPixels && settingStore.value.integrationsForm?.newsBreakPixels.length > 0 && settingStore.value.integrationsForm?.newsBreakPixels.some((pixel: string) => pixel && pixel.trim() !== '') ? settingStore.value.integrationsForm?.newsBreakPixels : [''],
              newsBreakPixelInputType: settingStore.value.integrationsForm?.newsBreakPixelInputType || 'pixel',
              newsBreakPixelInputDisabled: true, // 确保保持禁用状态
              bigoPixelInputs: settingStore.value.integrationsForm?.bigoPixels && settingStore.value.integrationsForm?.bigoPixels.length > 0 && settingStore.value.integrationsForm?.bigoPixels.some((pixel: string) => pixel && pixel.trim() !== '') ? settingStore.value.integrationsForm?.bigoPixels : [''],
              bigoPixelInputDisabled: true, // 确保保持禁用状态
              roaiPixelInputs: settingStore.value.integrationsForm?.roaiPixels && settingStore.value.integrationsForm?.roaiPixels.length > 0 && settingStore.value.integrationsForm?.roaiPixels.some((pixel: string) => pixel && pixel.trim() !== '') ? settingStore.value.integrationsForm?.roaiPixels : [''],
              roaiPixelInputDisabled: true, // 确保保持禁用状态
              pinterestInputDisabled: true,
              pinterestAccountIds,
              pinterestConversionAccessTokens,
              snapchatInputDisabled: true,
              snapchatPixelIds,
              snapchatConversionApiTokens,
              smartNewsInputDisabled: true,
              smartNewsPartnerNames,
              smartNewsPixelTagIds,
              smartNewsAuthorization:
                settingStore.value.integrationsForm?.smartNewsAuthorization || '',
              mediaGoInputDisabled: true,
              mediaGoAdIds,
              mediaGoAccountNames,
              googleAdsInputDisabled: true,
              googleAdsGIds,
              googleAdsConversionSendTos,
              isEnableGoogleAdsPostPurchaseEvent:
                settingStore.value.integrationsForm?.isEnableGoogleAdsPostPurchaseEvent ??
                false,
              applovinPixelInputs: settingStore.value.integrationsForm?.applovinPixelInputs?.length ? settingStore.value.integrationsForm.applovinPixelInputs : [{ productVendor: '', productCategory: '' }],
              applovinPixelIds,
              applovinPixelEventKeys,
              isEnableApplovinBackendReport:
                settingStore.value.integrationsForm?.isEnableApplovinBackendReport === true,
              postbackValuePercentage: settingStore.value.integrationsForm?.postbackValuePercentage ?? 100,
              extensionCode: settingStore.value.integrationsForm?.extensionCode || '',
              extensionCodeInputDisabled: true,
              isEnableExtensionCode: settingStore.value.integrationsForm?.isEnableExtensionCode ?? false,
              isEnablePostPurchaseEvent:
                settingStore.value.integrationsForm?.isEnablePostPurchaseEvent,
              googlePlacesApiKey: settingStore.value.integrationsForm?.googlePlacesApiKey,
              facebookConversionApiTokens: settingStore.value.integrationsForm?.facebookConversionApiTokens || [''],
            }
            integrationsState.value = {
              ...integrationsState.value,
              storefrontToken: storefrontToken || settingStore.value.integrationsForm?.storefrontToken,
              storefrontApiPrivateToken:
                storefrontApiPrivateToken ||
                settingStore.value.integrationsForm?.storefrontApiPrivateToken,
              facebookPixels: settingStore.value.integrationsForm?.facebookPixels,
              tikTokPixels: tikTokPixelInputs,
              tikTokConversionAccessTokens,
              newsBreakPixels: settingStore.value.integrationsForm?.newsBreakPixels || [],
              newsBreakPixelInputType: settingStore.value.integrationsForm?.newsBreakPixelInputType || 'pixel',
              bigoPixels: settingStore.value.integrationsForm?.bigoPixels || [],
              roaiPixels: settingStore.value.integrationsForm?.roaiPixels || [],
              pinterestAccountIds,
              pinterestConversionAccessTokens,
              snapchatPixelIds,
              snapchatConversionApiTokens,
              smartNewsPartnerNames,
              smartNewsPixelTagIds,
              smartNewsAuthorization:
                settingStore.value.integrationsForm?.smartNewsAuthorization || '',
              mediaGoAdIds,
              mediaGoAccountNames,
              googleAdsGIds,
              googleAdsConversionSendTos,
              isEnableGoogleAdsPostPurchaseEvent:
                settingStore.value.integrationsForm?.isEnableGoogleAdsPostPurchaseEvent ??
                false,
              applovinPixelInputs: settingStore.value.integrationsForm?.applovinPixelInputs?.length ? settingStore.value.integrationsForm.applovinPixelInputs : [{ productVendor: '', productCategory: '' }],
              applovinPixelId: applovinPixelIds[0] || '',
              applovinPixelEventKey: applovinPixelEventKeys[0] || '',
              isEnableApplovinBackendReport:
                settingStore.value.integrationsForm?.isEnableApplovinBackendReport === true,
              postbackValuePercentage: settingStore.value.integrationsForm?.postbackValuePercentage ?? 100,
              extensionCode: settingStore.value.integrationsForm?.extensionCode || '',
              extensionCodeInputDisabled: true,
              isEnableExtensionCode: settingStore.value.integrationsForm?.isEnableExtensionCode ?? false,
              isEnableConversionAPI: settingStore.value.integrationsForm?.isEnableConversionAPI,
              isEnableNBConversionAPI: settingStore.value.integrationsForm?.isEnableNBConversionAPI,
              isEnableBigoBackendReport:
                settingStore.value.integrationsForm?.isEnableBigoBackendReport === true,
              isEnablePinterestBackendConversion:
                settingStore.value.integrationsForm?.isEnablePinterestBackendConversion === true,
              isEnableSnapchatBackendReport:
                settingStore.value.integrationsForm?.isEnableSnapchatBackendReport === true,
              isEnableSmartNewsBackendConversion:
                settingStore.value.integrationsForm?.isEnableSmartNewsBackendConversion === true,
              isEnableMediaGoBackendConversion:
                settingStore.value.integrationsForm?.isEnableMediaGoBackendConversion === true,
              isEnableTikTokBackendReport:
                settingStore.value.integrationsForm?.isEnableTikTokBackendReport === true,
              isEnableEventPixelDetails:
                settingStore.value.integrationsForm?.isEnableEventPixelDetails,
              isEnablePostPurchaseEvent:
                settingStore.value.integrationsForm?.isEnablePostPurchaseEvent,
              isEnableShippingCostInPurchaseEvents:
                settingStore.value.integrationsForm?.isEnableShippingCostInPurchaseEvents,
              facebookADM: settingStore.value.integrationsForm?.facebookADM,
              tikTokADM: settingStore.value.integrationsForm?.tikTokADM,
              isValidToken: settingStore.value.integrationsForm?.isValidToken,
              isValidPrivateToken: settingStore.value.integrationsForm?.isValidPrivateToken,
              googlePlacesApiKey: settingStore.value.integrationsForm?.googlePlacesApiKey,
              facebookConversionApiTokens: settingStore.value.integrationsForm?.facebookConversionApiTokens || [''],
            }
            // 初始化 token（在初始化阶段，不会触发校验）
            token.value = storefrontToken || settingStore.value.integrationsForm?.storefrontToken
            privateToken.value =
              storefrontApiPrivateToken ||
              settingStore.value.integrationsForm?.storefrontApiPrivateToken
          }
          isValidToken.value = settingStore.value.integrationsForm?.isValidToken
          isValidPrivateToken.value = settingStore.value.integrationsForm?.isValidPrivateToken
        }

        // 记录初始token值，用于后续判断是否真正改变
        initialToken.value = token.value
        initialPrivateToken.value = privateToken.value

        // 所有数据设置完成后，重置标志位
        isDataLoading.value = false
        isInitializing.value = false
        hasUnsavedChanges.value = false
        nextTick(() => {
          isInitializingData.value = false
        })
      } catch (error) {

        // Fallback to ref values if store initialization fails
        const fallbackTikTokPixelInputs =
          integrationsState.value.tikTokPixels &&
          integrationsState.value.tikTokPixels.length > 0 &&
          integrationsState.value.tikTokPixels.some((pixel: string) => pixel && pixel.trim() !== '')
            ? integrationsState.value.tikTokPixels
            : ['']
        const fallbackTikTokConversionAccessTokens = normalizeTikTokConversionAccessTokens(
          integrationsState.value.tikTokConversionAccessTokens,
          fallbackTikTokPixelInputs.length
        )
        const fallbackPinterestAccountIds = normalizePinterestAccountIds(
          integrationsState.value.pinterestAccountIds
        )
        const fallbackPinterestConversionAccessTokens = normalizePinterestConversionAccessTokens(
          integrationsState.value.pinterestConversionAccessTokens,
          fallbackPinterestAccountIds.length
        )
        const {
          snapchatPixelIds: fallbackSnapchatPixelIds,
          snapchatConversionApiTokens: fallbackSnapchatConversionApiTokens,
        } = resolveSnapchatFields(integrationsState.value)
        const fallbackSmartNewsPartnerNames = normalizeSmartNewsPartnerNames(
          integrationsState.value.smartNewsPartnerNames
        )
        const fallbackSmartNewsPixelTagIds = normalizeSmartNewsPixelTagIds(
          integrationsState.value.smartNewsPixelTagIds,
          fallbackSmartNewsPartnerNames.length
        )
        const { mediaGoAdIds: fallbackMediaGoAdIds, mediaGoAccountNames: fallbackMediaGoAccountNames } =
          resolveMediaGoFields(integrationsState.value)
        const { googleAdsGIds: fallbackGoogleAdsGIds, googleAdsConversionSendTos: fallbackGoogleAdsConversionSendTos } =
          resolveGoogleAdsFields(integrationsState.value)
        state.value = {
          ...state.value,
          isEnableConversionAPI: integrationsState.value.isEnableConversionAPI,
          isEnableEventPixelDetails: integrationsState.value.isEnableEventPixelDetails,
          isEnableShippingCostInPurchaseEvents:
            integrationsState.value.isEnableShippingCostInPurchaseEvents,
          facebookADM: integrationsState.value.facebookADM,
          tikTokADM: integrationsState.value.tikTokADM,
          facebookPixelInputs: integrationsState.value.facebookPixels && integrationsState.value.facebookPixels.length > 0 && integrationsState.value.facebookPixels.some((pixel: string) => pixel && pixel.trim() !== '') ? integrationsState.value.facebookPixels : [''],
          facebookPixelInputDisabled: true, // 确保保持禁用状态
          tikTokPixelInputs: fallbackTikTokPixelInputs,
          tikTokPixelInputDisabled: true, // 确保保持禁用状态
          tikTokConversionAccessTokens: fallbackTikTokConversionAccessTokens,
          newsBreakPixelInputs: integrationsState.value.newsBreakPixels && integrationsState.value.newsBreakPixels.length > 0 && integrationsState.value.newsBreakPixels.some((pixel: string) => pixel && pixel.trim() !== '') ? integrationsState.value.newsBreakPixels : [''],
          newsBreakPixelInputType: integrationsState.value.newsBreakPixelInputType || 'pixel',
          newsBreakPixelInputDisabled: true, // 确保保持禁用状态
          bigoPixelInputs: integrationsState.value.bigoPixels && integrationsState.value.bigoPixels.length > 0 && integrationsState.value.bigoPixels.some((pixel: string) => pixel && pixel.trim() !== '') ? integrationsState.value.bigoPixels : [''],
          bigoPixelInputDisabled: true, // 确保保持禁用状态
          roaiPixelInputs: integrationsState.value.roaiPixels && integrationsState.value.roaiPixels.length > 0 && integrationsState.value.roaiPixels.some((pixel: string) => pixel && pixel.trim() !== '') ? integrationsState.value.roaiPixels : [''],
          roaiPixelInputDisabled: true, // 确保保持禁用状态
          pinterestInputDisabled: true,
          pinterestAccountIds: fallbackPinterestAccountIds,
          pinterestConversionAccessTokens: fallbackPinterestConversionAccessTokens,
          snapchatInputDisabled: true,
          snapchatPixelIds: fallbackSnapchatPixelIds,
          snapchatConversionApiTokens: fallbackSnapchatConversionApiTokens,
          smartNewsInputDisabled: true,
          smartNewsPartnerNames: fallbackSmartNewsPartnerNames,
          smartNewsPixelTagIds: fallbackSmartNewsPixelTagIds,
          smartNewsAuthorization: integrationsState.value.smartNewsAuthorization || '',
          mediaGoInputDisabled: true,
          mediaGoAdIds: fallbackMediaGoAdIds,
          mediaGoAccountNames: fallbackMediaGoAccountNames,
          googleAdsInputDisabled: true,
          googleAdsGIds: fallbackGoogleAdsGIds,
          googleAdsConversionSendTos: fallbackGoogleAdsConversionSendTos,
          isEnableGoogleAdsPostPurchaseEvent:
            integrationsState.value.isEnableGoogleAdsPostPurchaseEvent ?? false,
          extensionCode: integrationsState.value.extensionCode || '',
          extensionCodeInputDisabled: integrationsState.value.extensionCodeInputDisabled ?? true,
          isEnableExtensionCode: integrationsState.value.isEnableExtensionCode ?? false,
          googlePlacesApiKey: integrationsState.value.googlePlacesApiKey,
          isEnablePostPurchaseEvent: integrationsState.value.isEnablePostPurchaseEvent,
          isEnableNBConversionAPI: integrationsState.value.isEnableNBConversionAPI,
          isEnableBigoBackendReport: integrationsState.value.isEnableBigoBackendReport === true,
          isEnablePinterestBackendConversion:
            integrationsState.value.isEnablePinterestBackendConversion === true,
          isEnableSnapchatBackendReport:
            integrationsState.value.isEnableSnapchatBackendReport === true,
          isEnableSmartNewsBackendConversion:
            integrationsState.value.isEnableSmartNewsBackendConversion === true,
          isEnableMediaGoBackendConversion:
            integrationsState.value.isEnableMediaGoBackendConversion === true,
          isEnableTikTokBackendReport:
            integrationsState.value.isEnableTikTokBackendReport === true,
          facebookConversionApiTokens: integrationsState.value.facebookConversionApiTokens || [
            '',
          ],
        }
        token.value = integrationsState.value.storefrontToken
        privateToken.value = integrationsState.value.storefrontApiPrivateToken
        isValidToken.value = integrationsState.value.isValidToken
        isValidPrivateToken.value = integrationsState.value.isValidPrivateToken

        // 记录初始token值，用于后续判断是否真正改变
        initialToken.value = token.value
        initialPrivateToken.value = privateToken.value

        // 错误处理完成后，重置标志位
        isInitializing.value = false
        isDataLoading.value = false
        hasUnsavedChanges.value = false
        nextTick(() => {
          isInitializingData.value = false
        })
      }
    })
  })

  const validateToken = async () => {
    // 避免同一 token 短时间内重复校验
    if (
      token.value &&
      (token.value === lastValidatedToken.value || token.value === globalLastValidatedToken.value)
    ) {
      return
    }
    if (globalValidationInFlight.value) {
      return
    }
    if (!token.value) {
      isValidToken.value = false
      validationMessage.value = ''
      // Update integrationsState
      integrationsState.value = {
        ...integrationsState.value,
        storefrontToken: '',
        isValidToken: false,
      }
      updateStore(false) // 不设置未保存状态，因为这只是验证
      return
    }

    isChecking.value = true
    isValidToken.value = false
    validationMessage.value = ''
    lastValidatedToken.value = token.value
    globalLastValidatedToken.value = token.value
    globalValidationInFlight.value = true

    try {
      const response = await getStorefrontAuth(
        {},
        {
          headers: {
            'X-Shopify-Storefront-Access-Token': token.value,
          },
        }
      )
      if (response.isValid) {
        isValidToken.value = true
        validationMessage.value = response.message
      } else {
        isValidToken.value = false
        validationMessage.value = response.message || '验证失败，请检查token是否正确'
      }
    } catch (error) {
      isValidToken.value = false
      validationMessage.value = '验证失败，请检查token是否正确'
    } finally {
      isChecking.value = false
      globalValidationInFlight.value = false
      // Update integrationsState
      integrationsState.value = {
        ...integrationsState.value,
        storefrontToken: token.value,
        isValidToken: isValidToken.value,
      }
      updateStore(false) // 不设置未保存状态，因为这只是验证
    }
  }

  const debouncedValidateToken = debounce(validateToken, 500)

  const validatePrivateToken = async () => {
    if (
      privateToken.value &&
      (privateToken.value === lastValidatedPrivateToken.value ||
        privateToken.value === globalLastValidatedPrivateToken.value)
    ) {
      return
    }
    if (globalPrivateValidationInFlight.value) {
      return
    }
    if (!privateToken.value) {
      isValidPrivateToken.value = false
      privateValidationMessage.value = ''
      integrationsState.value = {
        ...integrationsState.value,
        storefrontApiPrivateToken: '',
        isValidPrivateToken: false,
      }
      updateStore(false)
      return
    }

    isCheckingPrivate.value = true
    isValidPrivateToken.value = false
    privateValidationMessage.value = ''
    lastValidatedPrivateToken.value = privateToken.value
    globalLastValidatedPrivateToken.value = privateToken.value
    globalPrivateValidationInFlight.value = true

    try {
      const response = await getStorefrontAuth(
        {},
        {
          headers: {
            'Shopify-Storefront-Private-Token': privateToken.value,
          },
        }
      )
      if (response.isValid) {
        isValidPrivateToken.value = true
        privateValidationMessage.value = response.message
      } else {
        isValidPrivateToken.value = false
        privateValidationMessage.value =
          response.message || '验证失败，请检查 token 是否正确'
      }
    } catch (error) {
      isValidPrivateToken.value = false
      privateValidationMessage.value = '验证失败，请检查 token 是否正确'
    } finally {
      isCheckingPrivate.value = false
      globalPrivateValidationInFlight.value = false
      integrationsState.value = {
        ...integrationsState.value,
        storefrontApiPrivateToken: privateToken.value,
        isValidPrivateToken: isValidPrivateToken.value,
      }
      updateStore(false)
    }
  }

  const debouncedValidatePrivateToken = debounce(validatePrivateToken, 500)

  // 过滤空值的辅助函数
  const filterEmptyValues = (array: string[]) => {
    return array.filter(item => item && item.trim() !== '')
  }

  const updateStore = (setUnsavedChanges = true) => {
    const store = useSettingIntegrationsStore()
    // const shopStore = useShopStore()
    try {
      // 过滤掉空的像素数据
      const filteredFacebookPixels = filterEmptyValues(state.value.facebookPixelInputs)
      const filteredTikTokPixels = filterEmptyValues(state.value.tikTokPixelInputs)
      const filteredNewsBreakPixels = filterEmptyValues(state.value.newsBreakPixelInputs)
      const filteredBigoPixels = filterEmptyValues(state.value.bigoPixelInputs)
      const filteredRoaiPixels = filterEmptyValues(state.value.roaiPixelInputs)
      const filteredGoogleAdsGIds = filterEmptyValues(state.value.googleAdsGIds)
      const filteredGoogleAdsConversionSendTos = state.value.googleAdsGIds.reduce<string[]>(
        (sendTos, gId, index) => {
          if (gId && gId.trim() !== '') {
            sendTos.push((state.value.googleAdsConversionSendTos[index] || '').trim())
          }
          return sendTos
        },
        []
      )
      const googleAdsGId = filteredGoogleAdsGIds[0] || ''
      const googleAdsConversionSendTo = filteredGoogleAdsConversionSendTos[0] || ''
      const filteredPinterestAccountIds = filterEmptyValues(state.value.pinterestAccountIds)
      const filteredPinterestConversionAccessTokens = state.value.pinterestAccountIds.reduce<string[]>(
        (tokens, accountId, index) => {
          if (accountId && accountId.trim() !== '') {
            tokens.push((state.value.pinterestConversionAccessTokens[index] || '').trim())
          }
          return tokens
        },
        []
      )
      const filteredSnapchatPixelIds = filterEmptyValues(state.value.snapchatPixelIds)
      const filteredSnapchatConversionApiTokens = state.value.snapchatPixelIds.reduce<string[]>(
        (tokens, pixelId, index) => {
          if (pixelId && pixelId.trim() !== '') {
            tokens.push((state.value.snapchatConversionApiTokens[index] || '').trim())
          }
          return tokens
        },
        []
      )
      const filteredSmartNewsPartnerNames = filterEmptyValues(state.value.smartNewsPartnerNames)
      const filteredSmartNewsPixelTagIds = state.value.smartNewsPartnerNames.reduce<string[]>(
        (pixelTagIds, partnerName, index) => {
          if (partnerName && partnerName.trim() !== '') {
            pixelTagIds.push((state.value.smartNewsPixelTagIds[index] || '').trim())
          }
          return pixelTagIds
        },
        []
      )
      const smartNewsAuthorization = state.value.smartNewsAuthorization.trim()
      const filteredMediaGoAdIds = filterEmptyValues(state.value.mediaGoAdIds)
      const filteredMediaGoAccountNames = state.value.mediaGoAdIds.reduce<string[]>(
        (accountNames, adId, index) => {
          if (adId && adId.trim() !== '') {
            accountNames.push((state.value.mediaGoAccountNames[index] || '').trim())
          }
          return accountNames
        },
        []
      )
      const mediaGoAdId = filteredMediaGoAdIds[0] || ''
      const mediaGoAccountName = filteredMediaGoAccountNames[0] || ''
      const filteredFacebookConversionApiTokens = filterEmptyValues(state.value.facebookConversionApiTokens)
      const filteredTikTokConversionAccessTokens = state.value.tikTokPixelInputs.reduce<string[]>(
        (tokens, pixel, index) => {
          if (pixel && pixel.trim() !== '') {
            tokens.push((state.value.tikTokConversionAccessTokens[index] || '').trim())
          }
          return tokens
        },
        []
      )
      // AppLovin 只支持单组：存为字符串，UI 仍用单元素数组展示
      const applovinPixelId = (state.value.applovinPixelIds[0] || '').trim()
      const applovinPixelEventKey = (state.value.applovinPixelEventKeys[0] || '').trim()

      const formData = {
        storefrontToken: token.value,
        storefrontApiPrivateToken: privateToken.value,
        facebookPixels: filteredFacebookPixels,
        tikTokPixels: filteredTikTokPixels,
        tikTokConversionAccessTokens: filteredTikTokConversionAccessTokens,
        newsBreakPixels: filteredNewsBreakPixels,
        newsBreakPixelInputType: state.value.newsBreakPixelInputType,
        bigoPixels: filteredBigoPixels,
        roaiPixels: filteredRoaiPixels,
        pinterestAccountIds: filteredPinterestAccountIds,
        pinterestConversionAccessTokens: filteredPinterestConversionAccessTokens,
        snapchatPixelIds: filteredSnapchatPixelIds,
        snapchatConversionApiTokens: filteredSnapchatConversionApiTokens,
        smartNewsPartnerNames: filteredSmartNewsPartnerNames,
        smartNewsPixelTagIds: filteredSmartNewsPixelTagIds,
        smartNewsAuthorization,
        mediaGoAdIds: filteredMediaGoAdIds,
        mediaGoAccountNames: filteredMediaGoAccountNames,
        mediaGoAdId,
        mediaGoAccountName,
        googleAdsGIds: filteredGoogleAdsGIds,
        googleAdsConversionSendTos: filteredGoogleAdsConversionSendTos,
        googleAdsGId,
        googleAdsConversionSendTo,
        isEnableGoogleAdsPostPurchaseEvent:
          state.value.isEnableGoogleAdsPostPurchaseEvent,
        applovinPixelInputs: state.value.applovinPixelInputs,
        applovinPixelEventKey,
        applovinPixelId,
        isEnableApplovinBackendReport: state.value.isEnableApplovinBackendReport,
        postbackValuePercentage: state.value.postbackValuePercentage,
        googlePlacesApiKey: state.value.googlePlacesApiKey,
        facebookADM: state.value.facebookADM,
        tikTokADM: state.value.tikTokADM,
        isEnableConversionAPI: state.value.isEnableConversionAPI,
        isEnableEventPixelDetails: state.value.isEnableEventPixelDetails,
        isEnablePostPurchaseEvent: state.value.isEnablePostPurchaseEvent,
        isEnableShippingCostInPurchaseEvents: state.value.isEnableShippingCostInPurchaseEvents,
        facebookConversionApiTokens: filteredFacebookConversionApiTokens,
        isValidToken: isValidToken.value,
        isValidPrivateToken: isValidPrivateToken.value,
        extensionCode: state.value.extensionCode,
        isEnableExtensionCode: state.value.isEnableExtensionCode,
        isEnableNBConversionAPI: state.value.isEnableNBConversionAPI,
        isEnableBigoBackendReport: state.value.isEnableBigoBackendReport,
        isEnablePinterestBackendConversion: state.value.isEnablePinterestBackendConversion,
        isEnableSnapchatBackendReport: state.value.isEnableSnapchatBackendReport,
        isEnableSmartNewsBackendConversion: state.value.isEnableSmartNewsBackendConversion,
        isEnableMediaGoBackendConversion: state.value.isEnableMediaGoBackendConversion,
        isEnableTikTokBackendReport: state.value.isEnableTikTokBackendReport,
      }
      store.setIntegrationsForm(formData)

      // 只有在非初始化阶段才标记有未保存的更改（避免页面一进来时 @update 或 watch 误触导致保存按钮可点）
      if (setUnsavedChanges && !isInitializingData.value) {
        hasUnsavedChanges.value = true
      }

      // Also update ref values
      integrationsState.value = {
        ...integrationsState.value,
        storefrontToken: token.value,
        storefrontApiPrivateToken: privateToken.value,
        facebookPixels: filteredFacebookPixels,
        tikTokPixels: filteredTikTokPixels,
        tikTokConversionAccessTokens: filteredTikTokConversionAccessTokens,
        newsBreakPixels: filteredNewsBreakPixels,
        newsBreakPixelInputType: state.value.newsBreakPixelInputType,
        bigoPixels: filteredBigoPixels,
        roaiPixels: filteredRoaiPixels,
        pinterestAccountIds: filteredPinterestAccountIds,
        pinterestConversionAccessTokens: filteredPinterestConversionAccessTokens,
        snapchatPixelIds: filteredSnapchatPixelIds,
        snapchatConversionApiTokens: filteredSnapchatConversionApiTokens,
        smartNewsPartnerNames: filteredSmartNewsPartnerNames,
        smartNewsPixelTagIds: filteredSmartNewsPixelTagIds,
        smartNewsAuthorization,
        mediaGoAdIds: filteredMediaGoAdIds,
        mediaGoAccountNames: filteredMediaGoAccountNames,
        mediaGoAdId,
        mediaGoAccountName,
        googleAdsGIds: filteredGoogleAdsGIds,
        googleAdsConversionSendTos: filteredGoogleAdsConversionSendTos,
        googleAdsGId,
        googleAdsConversionSendTo,
        isEnableGoogleAdsPostPurchaseEvent:
          state.value.isEnableGoogleAdsPostPurchaseEvent,
        applovinPixelInputs: state.value.applovinPixelInputs,
        applovinPixelEventKey,
        applovinPixelId,
        isEnableApplovinBackendReport: state.value.isEnableApplovinBackendReport,
        postbackValuePercentage: state.value.postbackValuePercentage,
        extensionCode: state.value.extensionCode,
        isEnableExtensionCode: state.value.isEnableExtensionCode,
        isEnableConversionAPI: state.value.isEnableConversionAPI,
        isEnableEventPixelDetails: state.value.isEnableEventPixelDetails,
        isEnablePostPurchaseEvent: state.value.isEnablePostPurchaseEvent,
        isEnableShippingCostInPurchaseEvents: state.value.isEnableShippingCostInPurchaseEvents,
        facebookADM: state.value.facebookADM,
        tikTokADM: state.value.tikTokADM,
        isValidToken: isValidToken.value,
        isValidPrivateToken: isValidPrivateToken.value,
        googlePlacesApiKey: state.value.googlePlacesApiKey,
        facebookConversionApiTokens: filteredFacebookConversionApiTokens,
        isEnableNBConversionAPI: state.value.isEnableNBConversionAPI,
        isEnableBigoBackendReport: state.value.isEnableBigoBackendReport,
        isEnablePinterestBackendConversion: state.value.isEnablePinterestBackendConversion,
        isEnableSnapchatBackendReport: state.value.isEnableSnapchatBackendReport,
        isEnableSmartNewsBackendConversion: state.value.isEnableSmartNewsBackendConversion,
        isEnableMediaGoBackendConversion: state.value.isEnableMediaGoBackendConversion,
        isEnableTikTokBackendReport: state.value.isEnableTikTokBackendReport,
      }
    } catch (error) {
      console.error('Error updating store:', error)
    }
  }

  // Watch for all form changes
  const debouncedUpdateStore = debounce(() => {
    if (isMounted.value && !isInitializing?.value && !isDataLoading.value && !isInitializingData.value) {
      updateStore(true) // 设置未保存状态
    }
  }, 500)

  // 添加一个标志来跟踪是否正在初始化
  const isInitializingData = ref(false)



  // Single watcher for all form data - 只监听实际的数据字段，不监听 UI 状态字段
  watch(
    [
      () => token.value,
      () => privateToken.value,
      () => state.value.facebookPixelInputs,
      () => state.value.tikTokPixelInputs,
      () => state.value.tikTokConversionAccessTokens,
      () => state.value.newsBreakPixelInputs,
      () => state.value.newsBreakPixelInputType,
      () => state.value.bigoPixelInputs,
      () => state.value.roaiPixelInputs,
      () => state.value.pinterestAccountIds,
      () => state.value.pinterestConversionAccessTokens,
      () => state.value.snapchatPixelIds,
      () => state.value.snapchatConversionApiTokens,
      () => state.value.smartNewsPartnerNames,
      () => state.value.smartNewsPixelTagIds,
      () => state.value.smartNewsAuthorization,
      () => state.value.mediaGoAdIds,
      () => state.value.mediaGoAccountNames,
      () => state.value.googleAdsGIds,
      () => state.value.googleAdsConversionSendTos,
      () => state.value.isEnableGoogleAdsPostPurchaseEvent,
      () => state.value.facebookADM,
      () => state.value.tikTokADM,
      () => state.value.isEnableConversionAPI,
      () => state.value.isEnableEventPixelDetails,
      () => state.value.googlePlacesApiKey,
      () => state.value.isEnablePostPurchaseEvent,
      () => state.value.isEnableShippingCostInPurchaseEvents,
      () => state.value.facebookConversionApiTokens,
      () => state.value.applovinPixelInputs,
      () => state.value.applovinPixelIds,
      () => state.value.applovinPixelEventKeys,
      () => state.value.postbackValuePercentage,
      () => state.value.isEnableApplovinBackendReport,
      () => state.value.extensionCode,
      () => state.value.isEnableExtensionCode,
      () => state.value.isEnableNBConversionAPI,
      () => state.value.isEnableBigoBackendReport,
      () => state.value.isEnablePinterestBackendConversion,
      () => state.value.isEnableSnapchatBackendReport,
      () => state.value.isEnableSmartNewsBackendConversion,
      () => state.value.isEnableMediaGoBackendConversion,
      () => state.value.isEnableTikTokBackendReport,
    ],
    () => {
      // 只有在完全初始化完成后才允许 watcher 触发，避免页面一进来就误触保存状态
      if (isMounted.value && !isInitializing.value && !isDataLoading.value && !isInitializingData.value) {
        debouncedUpdateStore()
      }
    },
    { deep: true }
  )

  // Special handling for token validation - 只有token真正改变时才校验
  watch(token, (newValue) => {
    // 只在用户主动改变token时才校验，初始化时不校验
    if (
      isMounted.value &&
      !isInitializing.value &&
      !isDataLoading.value &&
      !isInitializingData.value &&
      !isInitialValidating.value &&
      newValue !== initialToken.value // 只有当token与初始值不同时才校验
    ) {
      hasUserModifiedToken.value = true // 标记用户已修改token
      debouncedValidateToken()
    }
  })

  watch(privateToken, (newValue) => {
    if (
      isMounted.value &&
      !isInitializing.value &&
      !isDataLoading.value &&
      !isInitializingData.value &&
      !isInitialValidating.value &&
      newValue !== initialPrivateToken.value
    ) {
      hasUserModifiedPrivateToken.value = true
      debouncedValidatePrivateToken()
    }
  })

  // Watch for Conversion API enable/disable to sync token count with pixel count
  watch(() => state.value.isEnableConversionAPI, (isEnabled) => {
    if (isMounted.value && !isInitializing.value && !isDataLoading.value && !isInitializingData.value) {
      if (isEnabled) {
        // When enabling Conversion API, ensure token count matches pixel count
        const pixelCount = state.value.facebookPixelInputs.length
        const tokenCount = state.value.facebookConversionApiTokens.length

        if (tokenCount < pixelCount) {
          // Add missing tokens
          for (let i = tokenCount; i < pixelCount; i++) {
            state.value.facebookConversionApiTokens.push('')
          }
        } else if (tokenCount > pixelCount) {
          // Remove excess tokens
          state.value.facebookConversionApiTokens = state.value.facebookConversionApiTokens.slice(0, pixelCount)
        }
        updateStore(true)
      }
    }
  })

  // Watch for pixel input count changes to sync token count when Conversion API is enabled
  watch(() => state.value.facebookPixelInputs.length, (newCount) => {
    if (isMounted.value && !isInitializing.value && !isDataLoading.value && !isInitializingData.value && state.value.isEnableConversionAPI) {
      const tokenCount = state.value.facebookConversionApiTokens.length

      if (tokenCount < newCount) {
        // Add missing tokens
        for (let i = tokenCount; i < newCount; i++) {
          state.value.facebookConversionApiTokens.push('')
        }
      } else if (tokenCount > newCount) {
        // Remove excess tokens
        state.value.facebookConversionApiTokens = state.value.facebookConversionApiTokens.slice(0, newCount)
      }
      updateStore(true)
    }
  })

  // Watch for TikTok backend report enable/disable to sync token count with pixel count
  watch(() => state.value.isEnableTikTokBackendReport, (isEnabled) => {
    if (isMounted.value && !isInitializing.value && !isDataLoading.value && !isInitializingData.value) {
      if (isEnabled) {
        const pixelCount = state.value.tikTokPixelInputs.length
        const tokenCount = state.value.tikTokConversionAccessTokens.length

        if (tokenCount < pixelCount) {
          for (let i = tokenCount; i < pixelCount; i++) {
            state.value.tikTokConversionAccessTokens.push('')
          }
        } else if (tokenCount > pixelCount) {
          state.value.tikTokConversionAccessTokens =
            state.value.tikTokConversionAccessTokens.slice(0, pixelCount)
        }
        updateStore(true)
      }
    }
  })

  // Watch for TikTok pixel input count changes to sync token count when backend report is enabled
  watch(() => state.value.tikTokPixelInputs.length, (newCount) => {
    if (
      isMounted.value &&
      !isInitializing.value &&
      !isDataLoading.value &&
      !isInitializingData.value &&
      state.value.isEnableTikTokBackendReport
    ) {
      const tokenCount = state.value.tikTokConversionAccessTokens.length

      if (tokenCount < newCount) {
        for (let i = tokenCount; i < newCount; i++) {
          state.value.tikTokConversionAccessTokens.push('')
        }
      } else if (tokenCount > newCount) {
        state.value.tikTokConversionAccessTokens =
          state.value.tikTokConversionAccessTokens.slice(0, newCount)
      }
      updateStore(true)
    }
  })

  // extensionCode 不为空时强制勾选「已阅读」checkbox
  watch(
    () => state.value.extensionCode,
    (code) => {
      if (code?.trim()) {
        state.value.isEnableExtensionCode = true
      }
    }
  )

  const addFacebookPixelInput = () => {
    if (state.value.facebookPixelInputs.length < 20) {
      state.value.facebookPixelInputs.push('')
      state.value.facebookConversionApiTokens.push('')
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const removeFacebookPixelInput = (index: number) => {
    if (state.value.facebookPixelInputs.length > 1) {
      state.value.facebookPixelInputs.splice(index, 1)
      state.value.facebookConversionApiTokens.splice(index, 1)
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const addTikTokPixelInput = () => {
    if (state.value.tikTokPixelInputs.length < 11) {
      state.value.tikTokPixelInputs.push('')
      state.value.tikTokConversionAccessTokens.push('')
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const removeTikTokPixelInput = (index: number) => {
    if (state.value.tikTokPixelInputs.length > 1) {
      state.value.tikTokPixelInputs.splice(index, 1)
      state.value.tikTokConversionAccessTokens.splice(index, 1)
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const updateFacebookPixelInput = (index: number, value: string) => {
    state.value.facebookPixelInputs[index] = value
    updateStore(true) // 直接调用 updateStore，设置未保存状态
  }

  const updateTikTokPixelInput = (index: number, value: string) => {
    state.value.tikTokPixelInputs[index] = value
    updateStore(true) // 直接调用 updateStore，设置未保存状态
  }

  const updateTikTokConversionAccessToken = (index: number, value: string) => {
    state.value.tikTokConversionAccessTokens[index] = value
    updateStore(true)
  }

  const addTikTokConversionAccessToken = () => {
    if (state.value.tikTokConversionAccessTokens.length < 11) {
      state.value.tikTokConversionAccessTokens.push('')
      updateStore(true)
    }
  }

  const removeTikTokConversionAccessToken = (index: number) => {
    if (state.value.tikTokConversionAccessTokens.length > 1) {
      state.value.tikTokConversionAccessTokens.splice(index, 1)
      updateStore(true)
    }
  }

  const addNewsBreakPixelInput = () => {
    if (state.value.newsBreakPixelInputs.length < 20) {
      state.value.newsBreakPixelInputs.push('')
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const removeNewsBreakPixelInput = (index: number) => {
    if (state.value.newsBreakPixelInputs.length > 1) {
      state.value.newsBreakPixelInputs.splice(index, 1)
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const updateNewsBreakPixelInput = (index: number, value: string) => {
    state.value.newsBreakPixelInputs[index] = value
    updateStore(true) // 直接调用 updateStore，设置未保存状态
  }

  const validateNewsBreakPixel = async () => {
    const filteredPixels = filterEmptyValues(state.value.newsBreakPixelInputs)
    const testAdId = newsBreakTestAdId.value.trim()

    if (!filteredPixels.length) {
      isValidNewsBreakPixel.value = false
      newsBreakPixelValidationMessage.value = 'Please enter NewsBreak Pixel ID(s) first'
      return
    }

    if (!testAdId) {
      isValidNewsBreakPixel.value = false
      newsBreakPixelValidationMessage.value = 'Please enter Test Ad ID'
      return
    }

    isCheckingNewsBreakPixel.value = true
    isValidNewsBreakPixel.value = false
    newsBreakPixelValidationMessage.value = ''

    try {
      const response = await validatePixel({
        platform: 'newsbreak',
        config: {
          newsBreakPixels: filteredPixels,
          newsBreakPixelInputType: state.value.newsBreakPixelInputType,
        },
        testAdId,
      })

      isValidNewsBreakPixel.value = response.isValid
      if (response.isValid) {
        newsBreakPixelValidationMessage.value = 'Pixel validated successfully'
      } else {
        const failedIds = (response.results ?? [])
          .filter((result: PixelValidateResult) => !result.isValid)
          .map((result: PixelValidateResult) => result.id)
          .filter(Boolean)

        if (failedIds.length === 1) {
          newsBreakPixelValidationMessage.value =
            `Validation failed for ${failedIds[0]}. Please check your Pixel ID and Test Ad ID.`
        } else if (failedIds.length > 1) {
          newsBreakPixelValidationMessage.value =
            `Validation failed for ${failedIds.join(', ')}. Please check your Pixel IDs and Test Ad ID.`
        } else {
          newsBreakPixelValidationMessage.value =
            'Validation failed. Please check your Pixel ID and Test Ad ID.'
        }
      }
    } catch {
      isValidNewsBreakPixel.value = false
      newsBreakPixelValidationMessage.value =
        'Unable to validate pixel. Please try again later.'
    } finally {
      isCheckingNewsBreakPixel.value = false
    }
  }

  const addBigoPixelInput = () => {
    if (state.value.bigoPixelInputs.length < 20) {
      state.value.bigoPixelInputs.push('')
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const removeBigoPixelInput = (index: number) => {
    if (state.value.bigoPixelInputs.length > 1) {
      state.value.bigoPixelInputs.splice(index, 1)
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const updateBigoPixelInput = (index: number, value: string) => {
    state.value.bigoPixelInputs[index] = value
    updateStore(true) // 直接调用 updateStore，设置未保存状态
  }

  const addRoaiPixelInput = () => {
    if (state.value.roaiPixelInputs.length < 20) {
      state.value.roaiPixelInputs.push('')
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const removeRoaiPixelInput = (index: number) => {
    if (state.value.roaiPixelInputs.length > 1) {
      state.value.roaiPixelInputs.splice(index, 1)
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const updateRoaiPixelInput = (index: number, value: string) => {
    state.value.roaiPixelInputs[index] = value
    updateStore(true) // 直接调用 updateStore，设置未保存状态
  }

  const updateGoogleAdsConversionSendTo = (index: number, value: string) => {
    state.value.googleAdsConversionSendTos[index] = value
    updateStore(true)
  }

  const updateGoogleAdsGId = (index: number, value: string) => {
    state.value.googleAdsGIds[index] = value
    updateStore(true)
  }

  const addGoogleAdsAccount = () => {
    if (state.value.googleAdsGIds.length < 11) {
      state.value.googleAdsGIds.push('')
      state.value.googleAdsConversionSendTos.push('')
      updateStore(true)
    }
  }

  const removeGoogleAdsAccount = (index: number) => {
    if (state.value.googleAdsGIds.length > 1) {
      state.value.googleAdsGIds.splice(index, 1)
      state.value.googleAdsConversionSendTos.splice(index, 1)
      updateStore(true)
    }
  }

  const updatePinterestAccountId = (index: number, value: string) => {
    state.value.pinterestAccountIds[index] = value
    updateStore(true)
  }

  const updatePinterestConversionAccessToken = (index: number, value: string) => {
    state.value.pinterestConversionAccessTokens[index] = value
    updateStore(true)
  }

  const addPinterestAccount = () => {
    if (state.value.pinterestAccountIds.length < 11) {
      state.value.pinterestAccountIds.push('')
      state.value.pinterestConversionAccessTokens.push('')
      updateStore(true)
    }
  }

  const removePinterestAccount = (index: number) => {
    if (state.value.pinterestAccountIds.length > 1) {
      state.value.pinterestAccountIds.splice(index, 1)
      state.value.pinterestConversionAccessTokens.splice(index, 1)
      updateStore(true)
    }
  }

  const updateSnapchatPixelId = (index: number, value: string) => {
    state.value.snapchatPixelIds[index] = value
    updateStore(true)
  }

  const updateSnapchatConversionApiToken = (index: number, value: string) => {
    state.value.snapchatConversionApiTokens[index] = value
    updateStore(true)
  }

  const addSnapchatPixel = () => {
    if (state.value.snapchatPixelIds.length < 11) {
      state.value.snapchatPixelIds.push('')
      state.value.snapchatConversionApiTokens.push('')
      updateStore(true)
    }
  }

  const removeSnapchatPixel = (index: number) => {
    if (state.value.snapchatPixelIds.length > 1) {
      state.value.snapchatPixelIds.splice(index, 1)
      state.value.snapchatConversionApiTokens.splice(index, 1)
      updateStore(true)
    }
  }

  const updateSmartNewsPartnerName = (index: number, value: string) => {
    state.value.smartNewsPartnerNames[index] = value
    updateStore(true)
  }

  const updateSmartNewsPixelTagId = (index: number, value: string) => {
    state.value.smartNewsPixelTagIds[index] = value
    updateStore(true)
  }

  const updateSmartNewsAuthorization = (value: string) => {
    state.value.smartNewsAuthorization = value
    updateStore(true)
  }

  const addSmartNewsPartner = () => {
    if (state.value.smartNewsPartnerNames.length < 11) {
      state.value.smartNewsPartnerNames.push('')
      state.value.smartNewsPixelTagIds.push('')
      updateStore(true)
    }
  }

  const removeSmartNewsPartner = (index: number) => {
    if (state.value.smartNewsPartnerNames.length > 1) {
      state.value.smartNewsPartnerNames.splice(index, 1)
      state.value.smartNewsPixelTagIds.splice(index, 1)
      updateStore(true)
    }
  }

  const updateMediaGoAdId = (index: number, value: string) => {
    state.value.mediaGoAdIds[index] = value
    updateStore(true)
  }

  const updateMediaGoAccountName = (index: number, value: string) => {
    state.value.mediaGoAccountNames[index] = value
    updateStore(true)
  }

  const addMediaGoAccount = () => {
    if (state.value.mediaGoAdIds.length < 11) {
      state.value.mediaGoAdIds.push('')
      state.value.mediaGoAccountNames.push('')
      updateStore(true)
    }
  }

  const removeMediaGoAccount = (index: number) => {
    if (state.value.mediaGoAdIds.length > 1) {
      state.value.mediaGoAdIds.splice(index, 1)
      state.value.mediaGoAccountNames.splice(index, 1)
      updateStore(true)
    }
  }

  const updateFacebookConversionApiToken = (index: number, value: string) => {
    state.value.facebookConversionApiTokens[index] = value
    updateStore(true) // 直接调用 updateStore，设置未保存状态
  }

  const addFacebookConversionApiToken = () => {
    if (state.value.facebookConversionApiTokens.length < 20) {
      state.value.facebookConversionApiTokens.push('')
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const removeFacebookConversionApiToken = (index: number) => {
    if (state.value.facebookConversionApiTokens.length > 1) {
      state.value.facebookConversionApiTokens.splice(index, 1)
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  // 清除未保存更改状态
  const clearUnsavedChanges = () => {
    hasUnsavedChanges.value = false
  }

  const updateApplovinProductVendor = (index: number, value: string) => {
    state.value.applovinPixelInputs[index].productVendor = value
    updateStore(true) // 直接调用 updateStore，设置未保存状态
  }

  const updateApplovinPixelId = (index: number, value: string) => {
    state.value.applovinPixelIds[index] = value
    updateStore(true)
  }

  const updateApplovinPixelEventKey = (index: number, value: string) => {
    state.value.applovinPixelEventKeys[index] = value
    updateStore(true)
  }

  const updateApplovinProductCategory = (index: number, value: string) => {
    state.value.applovinPixelInputs[index].productCategory = value
    updateStore(true) // 直接调用 updateStore，设置未保存状态
  }

  const addApplovinInput = () => {
    if (state.value.applovinPixelInputs.length < 20) {
      state.value.applovinPixelInputs.push({
        productVendor: '',
        productCategory: '',
      })
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const removeApplovinInput = (index: number) => {
    if (state.value.applovinPixelInputs.length > 1) {
      state.value.applovinPixelInputs.splice(index, 1)
      updateStore(true) // 直接调用 updateStore，设置未保存状态
    }
  }

  const updateExtensionCode = (value: string) => {
    state.value.extensionCode = value
    if (value?.trim()) {
      state.value.isEnableExtensionCode = true
    }
    updateStore(true)
  }

  const updateIsEnableExtensionCode = (value: boolean) => {
    state.value.isEnableExtensionCode = value
    updateStore(true)
  }

  return {
    state,
    token,
    privateToken,
    isChecking,
    isCheckingPrivate,
    isValidToken,
    isValidPrivateToken,
    validationMessage,
    privateValidationMessage,
    hasUserModifiedToken, // 导出以便模板使用
    hasUserModifiedPrivateToken,
    debouncedValidateToken,
    debouncedValidatePrivateToken,
    hasUnsavedChanges,
    clearUnsavedChanges,
    updateFacebookPixelInput,
    updateTikTokPixelInput,
    updateTikTokConversionAccessToken,
    addTikTokConversionAccessToken,
    removeTikTokConversionAccessToken,
    addFacebookPixelInput,
    removeFacebookPixelInput,
    addTikTokPixelInput,
    removeTikTokPixelInput,
    updateNewsBreakPixelInput,
    addNewsBreakPixelInput,
    removeNewsBreakPixelInput,
    newsBreakTestAdId,
    isCheckingNewsBreakPixel,
    isValidNewsBreakPixel,
    newsBreakPixelValidationMessage,
    validateNewsBreakPixel,
    updateBigoPixelInput,
    addBigoPixelInput,
    removeBigoPixelInput,
    updateRoaiPixelInput,
    addRoaiPixelInput,
    removeRoaiPixelInput,
    updateGoogleAdsConversionSendTo,
    updateGoogleAdsGId,
    addGoogleAdsAccount,
    removeGoogleAdsAccount,
    updatePinterestAccountId,
    updatePinterestConversionAccessToken,
    addPinterestAccount,
    removePinterestAccount,
    updateSnapchatPixelId,
    updateSnapchatConversionApiToken,
    addSnapchatPixel,
    removeSnapchatPixel,
    updateSmartNewsPartnerName,
    updateSmartNewsPixelTagId,
    updateSmartNewsAuthorization,
    addSmartNewsPartner,
    removeSmartNewsPartner,
    updateMediaGoAdId,
    updateMediaGoAccountName,
    addMediaGoAccount,
    removeMediaGoAccount,
    updateApplovinPixelId,
    updateApplovinPixelEventKey,
    updateApplovinProductVendor,
    updateApplovinProductCategory,
    addApplovinInput,
    removeApplovinInput,
    updateExtensionCode,
    updateIsEnableExtensionCode,
    updateFacebookConversionApiToken,
    addFacebookConversionApiToken,
    removeFacebookConversionApiToken,
  }
}

// 供不需要初始化整个 useIntegrations 的组件（如头部保存按钮）引用未保存状态
export const integrationsUnsavedChanges = globalHasUnsavedChanges
export const clearIntegrationsUnsavedChanges = () => {
  globalHasUnsavedChanges.value = false
}
